import { useState, useCallback } from 'react';
import { useEditorContext } from '../contexts/editor-context';
import { Caption } from '../types';
import { useCaptions } from './use-captions';
import { COMP_NAME } from '../../../constants';
import { elevenLabsTranscriptToCaptions } from '../utils/captions/elevenlabs-to-captions';
import { MediaUrlResolver } from '../utils/general/media-url-resolver';
import { getUploadStatusForOverlays, waitForPendingUploads } from '../utils/general/upload-status-checker';

interface GenerateCaptionsResult {
  success: boolean;
  captions?: Caption[];
  srt?: string;
  language?: string;
  error?: string;
}

/**
 * Custom hook for generating captions from timeline composition using ElevenLabs API
 *
 * OPTIMIZED FLOW (70-85% faster):
 * 1. Single API call to /generate-captions endpoint
 * 2. Server renders audio-only (codec: mp3, skips video frames)
 * 3. Server calls ElevenLabs directly
 * 4. Returns captions in response (no polling, no download, no base64)
 */
export const useTimelineCaptions = () => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationError, setGenerationError] = useState<string | null>(null);
  const [lastGenerationResult, setLastGenerationResult] = useState<GenerateCaptionsResult | null>(null);

  const { parseSRT } = useCaptions();
  const {
    durationInFrames,
    fps,
    overlays,
    playerDimensions,
    backgroundColor,
    showAlignmentGuides,
    baseUrl,
  } = useEditorContext();

  /**
   * Main function to generate captions from timeline
   * Uses optimized server-side flow: audio-only render + direct ElevenLabs call
   */
  const generateCaptions = useCallback(async (language?: string): Promise<GenerateCaptionsResult> => {
    setIsGenerating(true);
    setGenerationError(null);
    setLastGenerationResult(null);

    const startTime = Date.now();

    try {
      // Step 1: Check for pending uploads and wait if needed
      console.log('[useTimelineCaptions] Checking upload status...');
      const uploadStatus = await getUploadStatusForOverlays(overlays);

      if (uploadStatus.hasPendingUploads) {
        console.log(
          `[useTimelineCaptions] Found ${uploadStatus.progress.pending} pending upload(s), waiting for completion...`
        );

        await waitForPendingUploads(overlays, (progress) => {
          console.log(
            `[useTimelineCaptions] Upload progress: ${progress.completed}/${progress.total} complete`
          );
        });

        console.log('[useTimelineCaptions] All uploads completed');
      } else {
        console.log('[useTimelineCaptions] No pending uploads, proceeding');
      }

      // Step 2: Resolve overlay URLs before rendering
      // This ensures blob URLs are converted to Convex HTTPS URLs where possible
      console.log('[useTimelineCaptions] Resolving overlay URLs...');
      const { overlays: resolvedOverlays, warnings } =
        await MediaUrlResolver.resolveAllOverlays(overlays, { strictMode: true });

      if (warnings.length > 0) {
        console.warn('[useTimelineCaptions] URL resolution warnings:', warnings);
        warnings.forEach(warning => console.warn(`  - ${warning}`));
      }

      // Step 3: Prepare inputProps for the composition
      const inputProps = {
        overlays: resolvedOverlays,
        durationInFrames,
        fps,
        // Remotion requires integer dimensions for calculateMetadata()
        width: Math.round(playerDimensions.width),
        height: Math.round(playerDimensions.height),
        showAlignmentGuides: showAlignmentGuides ?? true,
        backgroundColor: backgroundColor ?? 'white',
        baseUrl: baseUrl,
      };

      console.log('[useTimelineCaptions] Starting optimized caption generation...');
      console.log('[useTimelineCaptions] Composition:', {
        id: COMP_NAME,
        durationInFrames,
        fps,
        dimensions: `${playerDimensions.width}x${playerDimensions.height}`,
        overlayCount: resolvedOverlays.length,
      });

      // Step 4: Single API call - server handles audio render + transcription
      const response = await fetch('/api/latest/ssr/generate-captions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: COMP_NAME,
          language: language && language !== 'auto' ? language : undefined,
          inputProps,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Caption generation failed: ${response.status} - ${errorText}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Caption generation failed');
      }

      const networkTime = Date.now() - startTime;
      console.log(`[useTimelineCaptions] Server response received in ${networkTime}ms`);
      console.log(`[useTimelineCaptions] Server timing: ${result.timing?.totalMs}ms`);

      // Step 5: Process captions - prefer word-level from transcript
      let captions: Caption[] | null = null;

      // Try to use server-provided captions first (already parsed)
      if (result.captions && result.captions.length > 0) {
        captions = result.captions;
        console.log(`[useTimelineCaptions] Using server-provided captions: ${captions.length} segments`);
      }

      // Fallback: parse transcript for word-level timing
      if (!captions && result.transcript) {
        try {
          const transcriptObj = JSON.parse(result.transcript);
          const fromWords = elevenLabsTranscriptToCaptions(transcriptObj, {
            timeThreshold: 0.5,
            usePunctuation: true,
            maxGroupDuration: 2.0,
          });
          if (fromWords.length > 0) {
            captions = fromWords;
            console.log(`[useTimelineCaptions] Parsed word-level captions: ${captions.length} segments`);
          }
        } catch (e) {
          console.warn('[useTimelineCaptions] Failed to parse transcript for word-level timing');
        }
      }

      // Final fallback: parse SRT
      if (!captions && result.srt) {
        const parseResult = await parseSRT(result.srt);
        if (parseResult.success && parseResult.captions) {
          captions = parseResult.captions;
          console.log(`[useTimelineCaptions] Parsed SRT captions: ${captions.length} segments`);
        }
      }

      if (!captions || captions.length === 0) {
        throw new Error('No captions generated from transcription');
      }

      const totalTime = Date.now() - startTime;
      console.log(`[useTimelineCaptions] Caption generation complete in ${totalTime}ms`);

      const generationResult: GenerateCaptionsResult = {
        success: true,
        captions: captions,
        srt: result.srt,
        language: result.language,
      };

      setLastGenerationResult(generationResult);
      setIsGenerating(false);

      return generationResult;

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      console.error('[useTimelineCaptions] Error:', errorMessage);

      setGenerationError(errorMessage);
      setIsGenerating(false);

      const errorResult: GenerateCaptionsResult = {
        success: false,
        error: errorMessage,
      };

      setLastGenerationResult(errorResult);
      return errorResult;
    }
  }, [overlays, durationInFrames, fps, playerDimensions, showAlignmentGuides, backgroundColor, baseUrl, parseSRT]);

  /**
   * Reset the hook state
   */
  const reset = useCallback(() => {
    setIsGenerating(false);
    setGenerationError(null);
    setLastGenerationResult(null);
  }, []);

  return {
    isGenerating,
    generationError,
    lastGenerationResult,
    generateCaptions,
    reset,
  };
};

